'use client';

import { useCallback, useEffect, useState } from 'react';
import { ReactFlowProvider } from '@xyflow/react';
import StatusBar from '@/components/StatusBar';
import { api } from '@/lib/api';
import { HealthResponse, QueueStats } from '@/lib/types';
import PipelineCanvas from '@/components/pipeline/PipelineCanvas';
import NodePalette from '@/components/pipeline/NodePalette';
import RunControls from '@/components/pipeline/RunControls';
import PipelineMenu from '@/components/pipeline/PipelineMenu';
import RunHistoryPanel from '@/components/pipeline/RunHistoryPanel';

export default function PipelinePage() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [historyOpen, setHistoryOpen] = useState(true);

  // Restore the panel's collapsed state (set after mount to avoid hydration mismatch).
  useEffect(() => {
    if (localStorage.getItem('pipeline:historyOpen') === '0') setHistoryOpen(false);
  }, []);

  const toggleHistory = useCallback(() => {
    setHistoryOpen((o) => {
      const next = !o;
      try {
        localStorage.setItem('pipeline:historyOpen', next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [h, s] = await Promise.all([api.getHealth(), api.getQueueStats()]);
      setHealth(h);
      setStats(s);
    } catch {
      /* status bar is best-effort */
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 10000);
    return () => clearInterval(interval);
  }, [refresh]);

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <StatusBar health={health} stats={stats} onRefresh={refresh} />

      <div className="flex-1 flex overflow-hidden">
        <aside className="hidden md:block w-56 border-r border-[var(--border-subtle)] overflow-y-auto">
          <NodePalette />
        </aside>

        <main className="flex-1 min-w-0 relative">
          <div className="absolute top-4 left-4 z-10">
            <PipelineMenu />
          </div>
          <div className="absolute top-4 right-4 z-10">
            <RunControls />
          </div>
          <ReactFlowProvider>
            <PipelineCanvas />
          </ReactFlowProvider>
        </main>

        {historyOpen ? (
          <aside className="hidden lg:block w-64 border-l border-[var(--border-subtle)] overflow-y-auto">
            <RunHistoryPanel onCollapse={toggleHistory} />
          </aside>
        ) : (
          <aside className="hidden lg:flex w-9 border-l border-[var(--border-subtle)] flex-col items-center pt-3 shrink-0">
            <button
              onClick={toggleHistory}
              title="Show run history"
              className="flex flex-col items-center gap-2 text-[var(--text-muted)] hover:text-[var(--accent)] transition-subtle"
            >
              <span className="text-sm leading-none">‹</span>
              <span className="text-[10px] uppercase tracking-wider [writing-mode:vertical-rl]">
                Run history
              </span>
            </button>
          </aside>
        )}
      </div>
    </div>
  );
}
